// Thin fetch helpers for the new Postgres-backed Project/Task API.
// Deliberately independent from src/graphService.js / DataContext.jsx —
// this section doesn't read or write SharePoint at all.

// Set once (PmShell, on mount) so every mutating call below can attribute
// itself to a real person for the audit log, without every call site having
// to thread accounts[0] through manually.
import { setServerDirectory } from "../../peopleDb.js";
let currentUser = { email: null, name: null };
let authTokenProvider = null;
let authGeneration = 0;
let fileSessionQueue = Promise.resolve();
function assertAuthGeneration(generation) {
  if (generation !== authGeneration) throw new Error('Authentication account changed');
}
let fileSession = { token: null, expiresAt: 0, promise: null };
async function ensureFileSession(token, generation = authGeneration) {
  assertAuthGeneration(generation);
  if (!token) return token;
  if (fileSession.token === token && fileSession.expiresAt > Date.now() + 60000) return token;
  if (fileSession.token === token && fileSession.promise) {
    await fileSession.promise; assertAuthGeneration(generation); return token;
  }
  const pending = fileSessionQueue.then(async () => {
    assertAuthGeneration(generation);
    const response = await fetch('/api/file-session', { method: 'POST', credentials: 'same-origin', headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) {
      const error = new Error('Unable to establish file access');
      error.status = response.status;
      throw error;
    }
    const data = await response.json();
    assertAuthGeneration(generation);
    if (fileSession.token === token) fileSession.expiresAt = Date.now() + data.expiresIn * 1000;
  });
  fileSessionQueue = pending.catch(() => {});
  fileSession = { token, expiresAt: 0, promise: pending };
  try { await pending; } finally { if (fileSession.promise === pending) fileSession.promise = null; }
  return token;
}
export async function clearFileSession() {
  authGeneration++;
  authTokenProvider = null;
  authTokenCache = { token: null, expiresAt: 0, promise: null };
  const pending = fileSessionQueue.then(async () => {
    const response = await fetch('/api/file-session', { method: 'DELETE', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to clear file session');
    fileSession = { token: null, expiresAt: 0, promise: null };
  });
  fileSessionQueue = pending.catch(() => {});
  await pending;
}
let authTokenCache = { token: null, expiresAt: 0, promise: null };
const getCache = new Map();
const SESSION_CACHE_PREFIX = "pmApiCache:";

function cacheTtlFor(url) {
  if (url.startsWith("/api/people-list")) return 10 * 60 * 1000;
  if (url.startsWith("/api/work-types")) return 10 * 60 * 1000;
  if (url.startsWith("/api/project-templates")) return 10 * 60 * 1000;
  if (url.startsWith("/api/task-templates")) return 10 * 60 * 1000;
  if (url.startsWith("/api/teams")) return 5 * 60 * 1000;
  if (url.startsWith("/api/resource-tracked-people")) return 5 * 60 * 1000;
  if (url.startsWith("/api/projects?id=")) return 60 * 1000;
  if (url.startsWith("/api/projects")) return 30 * 1000;
  if (url.startsWith("/api/boards")) return 60 * 1000;
  if (url.startsWith("/api/task-statuses")) return 60 * 1000;
  if (url.startsWith("/api/automation-rules")) return 60 * 1000;
  if (url.startsWith("/api/sprints")) return 30 * 1000;
  if (url.startsWith("/api/tasks")) return 10 * 1000;
  if (url.startsWith("/api/workload")) return 30 * 1000;
  if (url.startsWith("/api/critical-path")) return 60 * 1000;
  if (url.startsWith("/api/expenses")) return 30 * 1000;
  if (url.startsWith("/api/audit-log")) return 30 * 1000;
  if (url.startsWith("/api/project-notes")) return 20 * 1000;
  if (url.startsWith("/api/project-schedule-import")) return 30 * 1000;
  if (url.startsWith("/api/sharepoint-recent-files")) return 60 * 1000;
  if (url.startsWith("/api/project-members")) return 60 * 1000;
  if (url.startsWith("/api/user-settings")) return 5 * 60 * 1000;
  if (url.startsWith("/api/my-tasks")) return 30 * 1000;
  if (url.startsWith("/api/pre-project-control")) return 15 * 1000;
  return 15 * 1000;
}

function shouldPersistGet(url) {
  return (
    url.startsWith("/api/people-list") ||
    url.startsWith("/api/work-types") ||
    url.startsWith("/api/project-templates") ||
    url.startsWith("/api/task-templates") ||
    url.startsWith("/api/teams") ||
    url.startsWith("/api/resource-tracked-people") ||
    url.startsWith("/api/user-settings")
  );
}

function readSessionCache(cacheKey) {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_PREFIX + cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(SESSION_CACHE_PREFIX + cacheKey);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeSessionCache(cacheKey, data, expiresAt) {
  try {
    sessionStorage.setItem(SESSION_CACHE_PREFIX + cacheKey, JSON.stringify({ data, expiresAt }));
  } catch {
    // Quota can be tight in some browsers; memory cache still works.
  }
}

export function clearGetCaches() {
  getCache.clear();
  try {
    Object.keys(sessionStorage)
      .filter(key => key.startsWith(SESSION_CACHE_PREFIX))
      .forEach(key => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

function jwtExpiresAt(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, "=")));
    return payload.exp ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

export function setCurrentUser(email, name) {
  currentUser = { email, name };
}

export function setAuthTokenProvider(provider) {
  authGeneration++;
  fileSession = { token: null, expiresAt: 0, promise: null };
  authTokenProvider = provider;
  authTokenCache = { token: null, expiresAt: 0, promise: null };
}

async function acquireAuthToken({ forceRefresh = false } = {}) {
  if (!authTokenProvider) return null;
  const generation = authGeneration;
  const now = Date.now();
  if (!forceRefresh && authTokenCache.token && authTokenCache.expiresAt - 120000 > now) return ensureFileSession(authTokenCache.token, generation);
  if (authTokenCache.promise) return authTokenCache.promise.then(token => ensureFileSession(token, generation));
  authTokenCache.promise = authTokenProvider({ forceRefresh })
    .then(token => {
      assertAuthGeneration(generation);
      const expiresAt = jwtExpiresAt(token) || Date.now() + 10 * 60 * 1000;
      authTokenCache = { token, expiresAt, promise: null };
      return token;
    })
    .catch(err => {
      if (generation === authGeneration) authTokenCache = { token: null, expiresAt: 0, promise: null };
      throw err;
    });
  return authTokenCache.promise.then(token => ensureFileSession(token, generation));
}

// File-session authentication must recover before any dependent API request.
export async function getAuthToken({ forceRefresh = false } = {}) {
  const generation = authGeneration;
  try {
    return await acquireAuthToken({ forceRefresh });
  } catch (error) {
    assertAuthGeneration(generation);
    if (error.status !== 401 || forceRefresh) throw error;
    return acquireAuthToken({ forceRefresh: true });
  }
}

async function request(url, { method = "GET", body, skipCache = false, ...rest } = {}) {
  let finalUrl = url;
  let finalBody;
  let token = await getAuthToken();

  if (method === "GET" || method === "DELETE") {
    const actorQs = new URLSearchParams({
      ...(currentUser.email ? { actorEmail: currentUser.email } : {}),
      ...(currentUser.name ? { actorName: currentUser.name } : {}),
    }).toString();
    if (actorQs) finalUrl += (url.includes("?") ? "&" : "?") + actorQs;
  } else if (body !== undefined) {
    finalBody = JSON.stringify({ ...body, actorEmail: currentUser.email, actorName: currentUser.name });
  }

  const cacheKey = method === "GET" && !skipCache ? finalUrl : null;
  const ttl = cacheKey ? cacheTtlFor(url) : 0;
  if (cacheKey) {
    const cached = getCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return cached.promise;
    if (shouldPersistGet(url)) {
      const sessionData = readSessionCache(cacheKey);
      if (sessionData !== null) {
        const promise = Promise.resolve(sessionData);
        getCache.set(cacheKey, { promise, expiresAt: Date.now() + ttl });
        return promise;
      }
    }
  } else if (method !== "GET") {
    clearGetCaches();
  }

  const doFetch = (bearerToken) => fetch(finalUrl, {
      ...rest,
      method,
      headers: {
        "Content-Type": "application/json",
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...(rest?.headers ?? {}),
      },
      ...(finalBody !== undefined ? { body: finalBody } : {}),
    });

  const promise = doFetch(token)
    .then(async res => {
      const data = await res.json().catch(() => ({}));
      if (
        res.status === 401 &&
        /token expired/i.test(String(data?.error ?? "")) &&
        authTokenProvider
      ) {
        token = await getAuthToken({ forceRefresh: true }).catch(() => null);
        if (token) {
          const retryRes = await doFetch(token);
          const retryData = await retryRes.json().catch(() => ({}));
          if (!retryRes.ok) throw new Error(retryData?.error ?? `HTTP ${retryRes.status}`);
          return retryData;
        }
      }
      if (!res.ok) throw new Error(data?.error ?? `HTTP ${res.status}`);
      return data;
    })
    .catch(err => {
      if (cacheKey) getCache.delete(cacheKey);
      throw err;
    });
  if (cacheKey) {
    const expiresAt = Date.now() + ttl;
    getCache.set(cacheKey, { promise, expiresAt });
    if (shouldPersistGet(url)) {
      promise.then(data => writeSessionCache(cacheKey, data, expiresAt)).catch(() => {});
    }
  }
  const data = await promise;
  return data;
}

export const listProjects = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/projects${qs ? `?${qs}` : ""}`);
};
export const getProject = (id) => request(`/api/projects?id=${id}`);
export const createProject = (body) => request("/api/projects", { method: "POST", body });
export const updateProject = (id, body) => request(`/api/projects?id=${id}`, { method: "PATCH", body });
export const archiveProject = (id) => request(`/api/projects?id=${id}`, { method: "DELETE" });

export const getBoard = (projectId) => request(`/api/boards?projectId=${projectId}`);

export const listTasks = (projectId, params = {}) => {
  const qs = new URLSearchParams({ projectId, ...params }).toString();
  return request(`/api/tasks?${qs}`);
};
export const getTask = (id) => request(`/api/tasks?id=${id}`);
export const createTask = (body) => request("/api/tasks", { method: "POST", body });
export const updateTask = (id, body) => request(`/api/tasks?id=${id}`, { method: "PATCH", body });
export const deleteTask = (id) => request(`/api/tasks?id=${id}`, { method: "DELETE" });

export const addComment = (body) => request("/api/task-comments", { method: "POST", body });

export const getDependencies = (taskId) => request(`/api/task-dependencies?taskId=${taskId}`);
export const listProjectDependencies = (projectId) => request(`/api/task-dependencies?projectId=${projectId}`);
export const getCriticalPath = (projectId) => request(`/api/critical-path?projectId=${projectId}`);
export const addDependency = (dependentTaskId, blockingTaskId, type) =>
  request("/api/task-dependencies", { method: "POST", body: { dependentTaskId, blockingTaskId, type } });
export const removeDependency = (id) => request(`/api/task-dependencies?id=${id}`, { method: "DELETE" });

export const listSprints = (projectId) => request(`/api/sprints?projectId=${projectId}`);
export const getSprint = (id) => request(`/api/sprints?id=${id}`);
export const createSprint = (body) => request("/api/sprints", { method: "POST", body });
export const updateSprint = (id, body) => request(`/api/sprints?id=${id}`, { method: "PATCH", body });
export const deleteSprint = (id) => request(`/api/sprints?id=${id}`, { method: "DELETE" });

export const listPeople = async (projectId, options = {}) => {
  const people = await request(`/api/people-list${projectId ? `?projectId=${projectId}` : ""}`, options);
  if (!projectId) setServerDirectory(people);
  return people;
};
export const listPeopleHandovers = (filters = {}) => request(`/api/people-handovers?${new URLSearchParams(filters)}`, { skipCache: true });
export const assignPeopleHandover = (id, replacementEmail) => request("/api/people-handovers", { method: "PATCH", body: { id, replacementEmail } });
export const getPhoneRosterStatus = () => request("/api/phone-roster", { skipCache: true });
export const applyReviewedPhoneRoster = id => request("/api/phone-roster", { method: "PATCH", body: { id } });

export const listWorkTypes = () => request("/api/work-types");
export const createWorkType = (body) => request("/api/work-types", { method: "POST", body });

export const listProjectTemplates = () => request("/api/project-templates");
export const createProjectTemplate = (body) => request("/api/project-templates", { method: "POST", body });
export const deleteProjectTemplate = (id) => request(`/api/project-templates?id=${id}`, { method: "DELETE" });

export const listTaskTemplates = () => request("/api/task-templates");
export const createTaskTemplate = (body) => request("/api/task-templates", { method: "POST", body });
export const deleteTaskTemplate = (id) => request(`/api/task-templates?id=${id}`, { method: "DELETE" });

export const addTaskAttachment = (body) => request("/api/task-attachments", { method: "POST", body });
export const removeTaskAttachment = (id) => request(`/api/task-attachments?id=${id}`, { method: "DELETE" });

export const listExpenses = (projectId) => request(`/api/expenses?projectId=${projectId}`);
export const createExpense = (body) => request("/api/expenses", { method: "POST", body });
export const deleteExpense = (id) => request(`/api/expenses?id=${id}`, { method: "DELETE" });

export const listTeams = () => request("/api/teams");
export const getTeam = (id) => request(`/api/teams?id=${id}`);
export const createTeam = (body) => request("/api/teams", { method: "POST", body });
export const updateTeam = (id, body) => request(`/api/teams?id=${id}`, { method: "PATCH", body });
export const deleteTeam = (id) => request(`/api/teams?id=${id}`, { method: "DELETE" });
export const addTeamMember = (body) => request("/api/team-members", { method: "POST", body });
export const removeTeamMember = (id) => request(`/api/team-members?id=${id}`, { method: "DELETE" });

export const listAllProjectMemberEmails = () => request("/api/project-members");
export const listResourceTrackedPeople = () => request("/api/resource-tracked-people");
export const addResourceTrackedPerson = (body) => request("/api/resource-tracked-people", { method: "POST", body });
export const removeResourceTrackedPerson = (id) => request(`/api/resource-tracked-people?id=${id}`, { method: "DELETE" });

export const addExpenseAttachment = (body) => request("/api/expense-attachments", { method: "POST", body });
export const removeExpenseAttachment = (id) => request(`/api/expense-attachments?id=${id}`, { method: "DELETE" });

export const listMyTasks = (email, params = {}) => {
  const qs = new URLSearchParams({ email, ...params }).toString();
  return request(`/api/my-tasks?${qs}`);
};

export const getUserSettings = (email) => request(`/api/user-settings?email=${encodeURIComponent(email)}`);
export const updateUserSettings = (body) => request("/api/user-settings", { method: "PUT", body });

export const getPreProjectControl = (options = {}) => request("/api/pre-project-control", options);
export const savePreProjectControl = (body) => request("/api/pre-project-control", { method: "PUT", body });

export const listAuditLog = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/audit-log?${qs}`);
};

export const listProjectNotes = (projectId) => request(`/api/project-notes?projectId=${projectId}`);
export const addProjectNote = (body) => request("/api/project-notes", { method: "POST", body });
export const deleteProjectNote = (id) => request(`/api/project-notes?id=${id}`, { method: "DELETE" });

export const resolveSharePointSite = (url) => request("/api/sharepoint-resolve", { method: "POST", body: { url } });
export const getSharePointRecentFiles = (projectId) => request(`/api/sharepoint-recent-files?projectId=${projectId}`);

export const listTaskStatuses = (projectId) => request(`/api/task-statuses?projectId=${projectId}`);
export const createTaskStatus = (body) => request("/api/task-statuses", { method: "POST", body });
export const updateTaskStatus = (id, body) => request(`/api/task-statuses?id=${id}`, { method: "PATCH", body });
export const deleteTaskStatus = (id) => request(`/api/task-statuses?id=${id}`, { method: "DELETE" });

export const createColumn = (boardId, body) => request(`/api/boards?boardId=${boardId}`, { method: "POST", body });
export const updateColumn = (columnId, body) => request(`/api/boards?columnId=${columnId}`, { method: "PATCH", body });
export const deleteColumn = (columnId) => request(`/api/boards?columnId=${columnId}`, { method: "DELETE" });

export const getWorkload = (projectId) => request(`/api/workload${projectId ? `?projectId=${projectId}` : ""}`);

export const listAutomationRules = (projectId) => request(`/api/automation-rules?projectId=${projectId}`);
export const createAutomationRule = (body) => request("/api/automation-rules", { method: "POST", body });
export const updateAutomationRule = (id, body) => request(`/api/automation-rules?id=${id}`, { method: "PATCH", body });
export const deleteAutomationRule = (id) => request(`/api/automation-rules?id=${id}`, { method: "DELETE" });

export const listProjectMembers = (projectId) => request(`/api/project-members?projectId=${projectId}`);
export const addProjectMember = (body) => request("/api/project-members", { method: "POST", body });
export const updateProjectMemberRole = (id, role) => request(`/api/project-members?id=${id}`, { method: "PATCH", body: { role } });
export const removeProjectMember = (id) => request(`/api/project-members?id=${id}`, { method: "DELETE" });

export const getMyAccess = (projectId) => request(`/api/my-access?projectId=${projectId}`);

export const getProjectScheduleImport = (projectId, importId) => {
  const qs = new URLSearchParams({ projectId, ...(importId ? { importId } : {}) }).toString();
  return request(`/api/project-schedule-import?${qs}`, { skipCache: true });
};

export async function uploadProjectScheduleImport(projectId, file, { sheetName } = {}) {
  clearGetCaches();
  const token = await getAuthToken().catch(() => null);
  const qs = new URLSearchParams({
    projectId,
    ...(currentUser.email ? { actorEmail: currentUser.email } : {}),
    ...(currentUser.name ? { actorName: currentUser.name } : {}),
  }).toString();
  const form = new FormData();
  form.append("file", file);
  if (sheetName) form.append("sheetName", sheetName);

  const res = await fetch(`/api/project-schedule-import?${qs}`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error ?? `HTTP ${res.status}`);
  return data;
}

export const syncProjectScheduleTasks = (projectId, importId) =>
  request(`/api/project-schedule-import?projectId=${projectId}`, {
    method: "POST",
    body: { action: "syncTasks", importId },
  });
